import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import {
  AuditData,
  FeatureFlagListData,
  JsonEnvelope,
} from '@/client/types/feature-flags';

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const featureFlagKeys = {
  all: ['feature-flags'] as const,
  list: () => [...featureFlagKeys.all, 'list'] as const,
  audit: (limit: number) => [...featureFlagKeys.all, 'audit', limit] as const,
};

// ─── Flag List ─────────────────────────────────────────────────────────────
// `enabled` here is the statically resolved (any-level veto) value; it ignores
// per-request `conditions`. Surface conditions separately in the UI.

const useFeatureFlagListFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (): Promise<FeatureFlagListData> => {
    const res = await request<JsonEnvelope<FeatureFlagListData>>('/api/admin/feature-flags/list');
    return res.data;
  }, 'An unexpected error occurred while fetching feature flags');
};

export const useFeatureFlagListQuery = () => {
  const queryFn = useFeatureFlagListFn();
  return useQuery({
    queryKey: featureFlagKeys.list(),
    queryFn,
    // Short stale window — flags change under the operator and propagate within
    // ~one poll interval, so we want fresh data on revisit/refetch.
    staleTime: 1000 * 15,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
    // A 403 means the caller lacks `system.feature_flags.manage`; don't hammer.
    retry: (failureCount, error: any) => {
      if (error?.status === 403) return false;
      return failureCount < 2;
    },
  });
};

// ─── Audit Log ───────────────────────────────────────────────────────────────
// `limit` clamps to 1–500 on the backend. Newest first. `ts` is epoch seconds.

const useFeatureFlagAuditFn = () => {
  const request = useClientStore((s) => s.request);
  return withErrorHandling(async (limit: number = 50): Promise<AuditData> => {
    const res = await request<JsonEnvelope<AuditData>>(
      `/api/admin/feature-flags/audit?limit=${limit}`,
    );
    return res.data;
  }, 'An unexpected error occurred while fetching the audit log');
};

export const useFeatureFlagAuditQuery = (limit = 50, enabled = true) => {
  const queryFn = useFeatureFlagAuditFn();
  return useQuery({
    queryKey: featureFlagKeys.audit(limit),
    queryFn: () => queryFn(limit),
    enabled,
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    // The audit log requires `system.feature_flags.manage`; a 403 is a stable
    // permission verdict, so don't retry it.
    retry: (failureCount, error: any) => {
      if (error?.status === 403) return false;
      return failureCount < 2;
    },
  });
};
